import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const READ_OPT_V1 = process.env.REACT_APP_READ_OPT_V1 !== 'false';
const PAGINATION_V1 = process.env.REACT_APP_PAGINATION_V1 !== 'false';
const HYBRID_REFRESH_V1 = process.env.REACT_APP_HYBRID_REFRESH_V1 !== 'false';
const CACHE_TTL_V1 = process.env.REACT_APP_CACHE_TTL_V1 !== 'false';
const LARGE_UPLOAD_TIMEOUT_MS = 15 * 60 * 1000;

// Create axios instance with optimizations
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 20000, // 20s global timeout for Firebase latency resilience
});

// Simple in-memory cache for GET requests
const cache = new Map();
const CACHE_DURATION = 30000; // 30 seconds
const inFlightGetRequests = new Map();

const normalizeListResponse = (payload) => {
  if (payload && Array.isArray(payload.items)) {
    return payload.items;
  }
  return Array.isArray(payload) ? payload : [];
};

// Request interceptor to add caching
api.interceptors.request.use((config) => {
  const skipCache = config.noCache === true || !CACHE_TTL_V1;

  // Only cache GET requests
  if (config.method === 'get' && !skipCache) {
    const cacheKey = config.url + JSON.stringify(config.params);
    const cached = cache.get(cacheKey);

    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      // Return cached data
      config.adapter = () => Promise.resolve({
        data: cached.data,
        status: 200,
        statusText: 'OK (cached)',
        headers: {},
        config,
      });
    }
  }
  return config;
});

// Response interceptor to cache responses
api.interceptors.response.use((response) => {
  const skipCache = response.config.noCache === true || !CACHE_TTL_V1;

  // Cache GET responses
  if (response.config.method === 'get' && !skipCache) {
    const cacheKey = response.config.url + JSON.stringify(response.config.params);
    cache.set(cacheKey, {
      data: response.data,
      timestamp: Date.now(),
    });
  }
  return response;
});

// ===== FRONTEND LOGGER =====
// Sends important frontend events to backend for debugging
const frontendLogQueue = [];
let logFlushTimer = null;

export const frontendLogger = {
  // Log an event (batched and sent every 5 seconds)
  log: (event, userId, data = {}) => {
    const logEntry = {
      timestamp: new Date().toISOString(),
      event,
      userId,
      data,
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    frontendLogQueue.push(logEntry);
    console.log(`📝 Frontend Log: ${event}`, data);

    // Flush logs every 5 seconds (or immediately if queue is large)
    if (frontendLogQueue.length >= 10) {
      frontendLogger.flush();
    } else if (!logFlushTimer) {
      logFlushTimer = setTimeout(() => {
        frontendLogger.flush();
      }, 5000);
    }
  },

  // Immediately send all queued logs
  flush: async () => {
    if (logFlushTimer) {
      clearTimeout(logFlushTimer);
      logFlushTimer = null;
    }

    if (frontendLogQueue.length === 0) return;

    const logsToSend = [...frontendLogQueue];
    frontendLogQueue.length = 0;

    try {
      await api.post('/api/frontend-logs', { logs: logsToSend });
    } catch (error) {
      console.error('Failed to send frontend logs:', error);
      // Put logs back in queue on failure
      frontendLogQueue.push(...logsToSend);
    }
  },

  // Common event types
  EVENTS: {
    PAGE_LOAD: 'PAGE_LOAD',
    START_WORK: 'START_WORK',
    TAKE_BREAK: 'TAKE_BREAK',
    RESUME_WORK: 'RESUME_WORK',
    END_DAY: 'END_DAY',
    STATE_RESET: 'STATE_RESET',
    FIREBASE_SYNC: 'FIREBASE_SYNC',
    TIMER_UPDATE: 'TIMER_UPDATE',
    ERROR: 'ERROR',
    STATE_MISMATCH: 'STATE_MISMATCH'
  }
};

// Flush logs when page unloads
window.addEventListener('beforeunload', () => {
  frontendLogger.flush();
});

// ===== AUTH API =====
export const authAPI = {
  login: async (pin) => {
    const response = await api.post('/api/auth/login', { pin }, { timeout: 20000 });
    return response.data;
  },

  register: async (userData) => {
    const response = await api.post('/api/auth/register', userData);
    return response.data;
  },

  changePin: async (userId, oldPin, newPin) => {
    const response = await api.post('/api/auth/change-pin', {
      userId,
      oldPin,
      newPin,
    });
    return response.data;
  },
};

// ===== USER API =====
export const userAPI = {
  // Control Panel roster: everyone, including hidden (archived) accounts
  getAllAdmin: async () => {
    const response = await api.get('/api/users', {
      params: { includeArchived: true, includeDetails: true },
      noCache: true,
      timeout: 30000,
    });
    return normalizeListResponse(response.data);
  },

  create: async (userData) => {
    const response = await api.post('/api/users', userData);
    return response.data;
  },

  remove: async (userId) => {
    const response = await api.delete(`/api/users/${userId}`);
    return response.data;
  },

  archive: async (userId, archived = true) => {
    const response = await api.patch(`/api/users/${userId}/archive`, null, { params: { archived } });
    return response.data;
  },

  getAll: async (noCache = false, options = {}) => {
    const params = {};
    if (READ_OPT_V1) {
      if (options.limit != null) params.limit = options.limit;
      if (options.cursor) params.cursor = options.cursor;
      if (options.fields) params.fields = options.fields;
      if (typeof options.includeDetails === 'boolean') params.includeDetails = options.includeDetails;
      if (options.paginate && PAGINATION_V1) params.paginate = true;
    }
    const reqKey = JSON.stringify({ url: '/api/users', params, noCache });
    if (!noCache && inFlightGetRequests.has(reqKey)) return inFlightGetRequests.get(reqKey);
    const req = api.get('/api/users', { params, noCache, timeout: 30000 }).then((response) => {
      if (options.paginate && PAGINATION_V1) return response.data;
      return normalizeListResponse(response.data);
    }).finally(() => inFlightGetRequests.delete(reqKey));
    if (!noCache) inFlightGetRequests.set(reqKey, req);
    return req;
  },

  getById: async (userId) => {
    const response = await api.get(`/api/users/${userId}`);
    return response.data;
  },

  update: async (userId, userData) => {
    const response = await api.patch(`/api/users/${userId}`, userData);
    return response.data;
  },

  updateStatus: async (userId, isActive) => {
    const response = await api.patch(`/api/users/${userId}/status`, null, {
      params: { isActive },
    });
    return response.data;
  },

  updateOnlineStatus: async (userId, isOnline) => {
    const response = await api.patch(`/api/users/${userId}/online`, null, {
      params: { isOnline },
    });
    return response.data;
  },

  updateRedFlag: async (userId, redFlag, redFlagNote = '', redFlagRaisedAt = null) => {
    const response = await api.patch(`/api/users/${userId}`, {
      redFlag,
      redFlagNote,
      redFlagRaisedAt,
    });
    return response.data;
  },

  saveActiveWorkState: async (userId, workState) => {
    const response = await api.post(`/api/users/${userId}/active-work-state`, workState);
    return response.data;
  },

  getActiveWorkState: async (userId) => {
    const response = await api.get(`/api/users/${userId}/active-work-state`, { noCache: true });
    return response.data;
  },

  getActiveWorkStatesBatch: async (userIds) => {
    const response = await api.post('/api/active-work-states/batch', { userIds }, { timeout: 15000 });
    return response.data;
  },

  clearActiveWorkState: async (userId) => {
    const response = await api.delete(`/api/users/${userId}/active-work-state`);
    return response.data;
  },
};

// ===== SCHEDULE API =====
export const scheduleAPI = {
  create: async (scheduleData) => {
    const response = await api.post('/api/schedules', scheduleData);
    return response.data;
  },

  getAll: async (employeeId = null, date = null, options = {}) => {
    const buildParams = (cursor = null) => {
      const params = {};
      if (employeeId) params.employeeId = employeeId;
      if (date) params.date = date;
      if (READ_OPT_V1) {
        if (options.dateFrom) params.dateFrom = options.dateFrom;
        if (options.dateTo) params.dateTo = options.dateTo;
        if (options.limit != null) params.limit = options.limit;
        if (cursor) params.cursor = cursor;
        else if (options.cursor) params.cursor = options.cursor;
        if (options.fields) params.fields = options.fields;
        if ((options.paginate || options.fetchAll) && PAGINATION_V1) params.paginate = true;
      }
      return params;
    };

    if (options.fetchAll && READ_OPT_V1 && PAGINATION_V1) {
      const pageSize = options.limit != null ? options.limit : 200;
      let cursor = options.cursor || null;
      let guard = 0;
      const allItems = [];
      while (guard < 50) {
        const params = buildParams(cursor);
        params.limit = pageSize;
        const response = await api.get('/api/schedules', { params });
        const payload = response.data || {};
        const pageItems = Array.isArray(payload.items) ? payload.items : normalizeListResponse(payload);
        allItems.push(...pageItems);
        cursor = payload.nextCursor || null;
        guard += 1;
        if (!cursor) break;
      }
      return allItems;
    }

    const response = await api.get('/api/schedules', { params: buildParams() });
    if ((options.paginate || options.fetchAll) && PAGINATION_V1) return response.data;
    return normalizeListResponse(response.data);
  },

  update: async (scheduleId, scheduleData) => {
    const response = await api.patch(`/api/schedules/${scheduleId}`, scheduleData);
    return response.data;
  },

  delete: async (scheduleId) => {
    const response = await api.delete(`/api/schedules/${scheduleId}`);
    return response.data;
  },
};

// ===== SOCIAL POST API =====
export const socialPostAPI = {
  create: async (postData) => {
    const response = await api.post('/api/social-posts', postData);
    return response.data;
  },

  getAll: async (options = {}) => {
    const buildParams = (cursor = null) => {
      const params = {};
      if (READ_OPT_V1) {
        if (options.status) params.status = options.status;
        if (options.ownerId) params.ownerId = options.ownerId;
        if (options.platform) params.platform = options.platform;
        if (options.dateFrom) params.dateFrom = options.dateFrom;
        if (options.dateTo) params.dateTo = options.dateTo;
        if (options.limit != null) params.limit = options.limit;
        if (cursor) params.cursor = cursor;
        else if (options.cursor) params.cursor = options.cursor;
        if (options.fields) params.fields = options.fields;
        if ((options.paginate || options.fetchAll) && PAGINATION_V1) params.paginate = true;
      }
      return params;
    };

    if (options.fetchAll && READ_OPT_V1 && PAGINATION_V1) {
      const pageSize = options.limit != null ? options.limit : 200;
      let cursor = options.cursor || null;
      let guard = 0;
      const allItems = [];

      while (guard < 50) {
        const params = buildParams(cursor);
        params.limit = pageSize;
        const response = await api.get('/api/social-posts', { params });
        const payload = response.data || {};
        const pageItems = Array.isArray(payload.items) ? payload.items : normalizeListResponse(payload);
        allItems.push(...pageItems);
        cursor = payload.nextCursor || null;
        guard += 1;
        if (!cursor) break;
      }

      return allItems;
    }

    const response = await api.get('/api/social-posts', { params: buildParams() });
    if ((options.paginate || options.fetchAll) && PAGINATION_V1) return response.data;
    return normalizeListResponse(response.data);
  },

  update: async (postId, postData) => {
    const response = await api.patch(`/api/social-posts/${postId}`, postData);
    return response.data;
  },

  uploadMedia: async (file, options = {}) => {
    const { onUploadProgress, timeout = LARGE_UPLOAD_TIMEOUT_MS } = options;
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/api/social-posts/upload-media', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout,
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
      onUploadProgress,
    });
    return response.data;
  },

  cleanupMedia: async (mediaItems = []) => {
    const response = await api.post('/api/social-posts/cleanup-media', { mediaItems });
    return response.data;
  },

  downloadBackupZip: async () => api.get('/api/social-posts/backup-zip', {
    responseType: 'blob',
    timeout: LARGE_UPLOAD_TIMEOUT_MS,
    maxContentLength: Infinity,
    maxBodyLength: Infinity,
  }),

  delete: async (postId) => {
    const response = await api.delete(`/api/social-posts/${postId}`);
    return response.data;
  },
};

// ===== TASK API =====
export const taskAPI = {
  create: async (taskData) => {
    const response = await api.post('/api/tasks', taskData);
    return response.data;
  },

  getAll: async (assignedTo = null, options = {}) => {
    const params = {};
    if (assignedTo) params.assignedTo = assignedTo;
    if (READ_OPT_V1) {
      if (options.status) params.status = options.status;
      if (options.fields) params.fields = options.fields;
      if (typeof options.includeDetails === 'boolean') params.includeDetails = options.includeDetails;
      if (options.limit != null) params.limit = options.limit;
      if (options.cursor) params.cursor = options.cursor;
      if (options.paginate && PAGINATION_V1) params.paginate = true;
    }
    const response = await api.get('/api/tasks', { params });
    if (options.paginate && PAGINATION_V1) return response.data;
    return normalizeListResponse(response.data);
  },

  getWorkStreams: async () => {
    const response = await api.get('/api/workstreams');
    return response.data;
  },

  getById: async (taskId) => {
    const response = await api.get(`/api/tasks/${taskId}`);
    return response.data;
  },

  update: async (taskId, updateData) => {
    const response = await api.patch(`/api/tasks/${taskId}`, updateData);
    return response.data;
  },

  delete: async (taskId) => {
    const response = await api.delete(`/api/tasks/${taskId}`);
    return response.data;
  },

  addComment: async (taskId, comment) => {
    const response = await api.post(`/api/tasks/${taskId}/comments`, comment);
    return response.data;
  },

  addAttachment: async (taskId, attachment) => {
    const response = await api.post(`/api/tasks/${taskId}/attachments`, attachment);
    return response.data;
  },

  addSubtaskAttachment: async (taskId, subtaskPath, attachment) => {
    const response = await api.post(`/api/tasks/${taskId}/subtasks/${subtaskPath}/attachments`, attachment);
    return response.data;
  },

  uploadFile: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/api/upload-file', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: LARGE_UPLOAD_TIMEOUT_MS,
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
    });
    return response.data;
  },

  addSubtask: async (taskId, subtaskData) => {
    const response = await api.post(`/api/tasks/${taskId}/subtasks`, subtaskData);
    return response.data;
  },

  toggleSubtask: async (taskId, subtaskId, completed) => {
    const response = await api.patch(`/api/tasks/${taskId}/subtasks/${subtaskId}`, { completed });
    return response.data;
  },

  deleteSubtask: async (taskId, subtaskId) => {
    const response = await api.delete(`/api/tasks/${taskId}/subtasks/${subtaskId}`);
    return response.data;
  },

  getStatuses: async () => {
    const response = await api.get('/api/task-statuses', { timeout: 30000 });
    return response.data;
  },

  createStatus: async (statusData) => {
    const response = await api.post('/api/task-statuses', statusData);
    return response.data;
  },

  deleteStatus: async (statusId) => {
    const response = await api.delete(`/api/task-statuses/${statusId}`);
    return response.data;
  },
};

// ===== TIME TRACKING API =====
export const timeTrackingAPI = {
  clockIn: async (sessionData) => {
    const response = await api.post('/api/work-sessions', sessionData);
    return response.data;
  },

  getAll: async (
    employeeId = null,
    date = null,
    daysBack = 30,
    includeLocationTrail = false,
    noCache = false,
    options = {}
  ) => {
    const params = {};
    if (employeeId) params.employeeId = employeeId;
    if (date) params.date = date;
    if (daysBack) params.daysBack = daysBack;
    params.includeLocationTrail = includeLocationTrail;
    if (READ_OPT_V1) {
      if (options.dateFrom) params.dateFrom = options.dateFrom;
      if (options.dateTo) params.dateTo = options.dateTo;
      if (options.fields) params.fields = options.fields;
      if (options.limit != null) params.limit = options.limit;
      if (options.cursor) params.cursor = options.cursor;
      if (options.paginate && PAGINATION_V1) params.paginate = true;
    }
    const response = await api.get('/api/work-sessions', { params, noCache, timeout: 30000 });
    if (options.paginate && PAGINATION_V1) return response.data;
    return normalizeListResponse(response.data);
  },

  getLocationTrail: async (sessionId) => {
    const response = await api.get(`/api/work-sessions/${sessionId}/location-trail`);
    return response.data;
  },

  clockOut: async (sessionId, clockOut, clockOutDate, duration, clockOutFull = null, locationTrail = [], timeIntervals = [], durationSeconds = null) => {
    const response = await api.patch(`/api/work-sessions/${sessionId}/clock-out`, {
      clockOut,
      clockOutDate,  // Date of clock out for overnight sessions
      clockOutFull,  // 🌍 NEW: Full ISO datetime with timezone
      duration,
      durationSeconds,
      locationTrail,
      timeIntervals,
    });
    return response.data;
  },

  updateDayEnd: async (sessionId, summary, additionalTasks, fileUrl = null, fileName = null, locationTrail = [], timeIntervals = [], isAutoSaved = false) => {
    const response = await api.patch(`/api/work-sessions/${sessionId}/day-end`, {
      summary,
      additionalTasks,
      fileUrl,
      fileName,
      locationTrail,
      timeIntervals,
      isAutoSaved,
    });
    return response.data;
  },

  addFile: async (sessionId, fileUrl, fileName, uploadedBy) => {
    const response = await api.post(`/api/work-sessions/${sessionId}/files`, {
      fileUrl,
      fileName,
      uploadedBy,
    });
    return response.data;
  },

  deleteFile: async (sessionId, fileIndex) => {
    const response = await api.delete(`/api/work-sessions/${sessionId}/files/${fileIndex}`);
    return response.data;
  },

  updateSession: async (sessionId, updateData) => {
    const response = await api.patch(`/api/work-sessions/${sessionId}`, updateData);
    return response.data;
  },

  delete: async (sessionId) => {
    const response = await api.delete(`/api/work-sessions/${sessionId}`);
    return response.data;
  },

  approveAutoSave: async (sessionId) => {
    const response = await api.patch(`/api/work-sessions/${sessionId}/approve-autosave`);
    return response.data;
  },

  getStats: async (employeeId) => {
    const response = await api.get(`/api/work-sessions/stats/${employeeId}`, { timeout: 30000 });
    return response.data;
  },

  getTeamOverviewStats: async (noCache = false) => {
    const response = await api.get('/api/work-sessions/team-overview-stats', {
      noCache,
      timeout: 30000,
    });
    return response.data;
  },
};

// ===== CHAT/MESSAGE API =====
export const chatAPI = {
  getConversations: async (userId) => {
    const response = await api.get('/api/conversations', {
      params: { userId },
    });
    return response.data;
  },

  createConversation: async (conversationData) => {
    const response = await api.post('/api/conversations', conversationData);
    return response.data;
  },

  deleteConversation: async (conversationId) => {
    const response = await api.delete(`/api/conversations/${conversationId}`);
    return response.data;
  },

  getMessages: async (conversationId) => {
    const response = await api.get(`/api/conversations/${conversationId}/messages`);
    return response.data;
  },

  sendMessage: async (messageData) => {
    const response = await api.post('/api/messages', messageData);
    return response.data;
  },

  markConversationRead: async (conversationId, userId) => {
    const response = await api.patch(`/api/conversations/${conversationId}/mark-read`, null, {
      params: { userId },
    });
    return response.data;
  },
};

// ===== ANNOUNCEMENT API =====
export const announcementAPI = {
  create: async (announcementData) => {
    const response = await api.post('/api/announcements', announcementData);
    return response.data;
  },

  getAll: async () => {
    const response = await api.get('/api/announcements');
    return response.data;
  },

  delete: async (announcementId) => {
    const response = await api.delete(`/api/announcements/${announcementId}`);
    return response.data;
  },
};

// ===== FILE UPLOAD API =====
export const fileAPI = {
  upload: async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/api/upload-file', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: LARGE_UPLOAD_TIMEOUT_MS,
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
    });
    return response.data;
  },
};



// ===== ASSET API =====
export const assetAPI = {
  togglePurchased: async (firebaseId, isPurchased) => {
    const response = await api.patch(`/api/assets/purchased/${firebaseId}?is_purchased=${isPurchased}`);
    return response.data;
  },

  askTeam: async (firebaseId) => {
    const response = await api.patch(`/api/assets/ask/${firebaseId}`);
    return response.data;
  },

  voteOnAsset: async (firebaseId, voteType, userId) => {
    const response = await api.patch(`/api/assets/vote/${firebaseId}`, {
      type: voteType,
      userId
    });
    return response.data;
  },

  addComment: async (firebaseId, text, userName, userId) => {
    const response = await api.post(`/api/assets/comment/${firebaseId}`, {
      text,
      userName,
      userId
    });
    return response.data;
  },

  reactToComment: async (firebaseId, commentId, type, userId) => {
    const response = await api.patch(`/api/assets/comment/${firebaseId}/${commentId}/react`, {
      type,
      userId
    });
    return response.data;
  },
};

export default api;
